// ContentFilters.util.ts

import { waitForWindowProperty } from "./routerAdditives.util";
import { getEnabledModules, isStandardEnabled as isModuleEnabled } from "./EnabledModulesManager";

/**
 * The `ContentFilters` class provides methods to filter and manipulate content within a specified page view.
 * It allows filtering elements based on standards and MSP status, as well as replacing placeholders within the content.
 */

export class ContentFilters {
    private pageView: HTMLElement | null;
    private changedPage: boolean;
    private changedButton: boolean;

    /**
     * Initializes a new instance of the `ContentFilters` class.
     * @param pageView - The root HTMLElement within which content filtering will be performed.
     */
    constructor(pageView: HTMLElement) {
        this.pageView = pageView;
        this.changedPage = false;
        this.changedButton = false;
    }

    /**
     * Resets the internal flags tracking changes to the page and buttons.
     */
    private resetFlags(): void {
        this.changedPage = false;
        this.changedButton = false;
    }

    /**
     * Filters elements by a specified standard. Elements not matching the standard are hidden or removed.
     * @param customerPlan - The standard to filter elements by.
     */
    public async filterByStandard(customerPlan: string): Promise<void> {
        // Wait for complianceModules to be loaded before filtering elements
        let modules = await getEnabledModules();
        if (!modules) {
            console.error('No compliance modules found');
            await waitForWindowProperty('companyDetails.complianceModules').then((updatedModules) => {
                modules = updatedModules;
            });
        }

        // console.error('[Purposeful Error] Filtering by standard', modules);

        this.resetFlags();

        const elements = this.pageView.querySelectorAll<HTMLElement>('[data-standard],[data-module]');
        const normalizedCustomerPlan = customerPlan.toLowerCase().replaceAll(' ', '').trim()

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            {
                var standardAttr = element.getAttribute('data-standard')?.toLowerCase().replaceAll(' ', '').trim();
                const moduleAttr = element.getAttribute('data-module')?.trim().toLowerCase();

                let standardMatches = false;

                if (standardAttr !== undefined && standardAttr !== null) {
                    const standardParts = standardAttr.split(',');

                    for (let i = 0; i < standardParts.length; i++) {
                        if (standardParts[i] === "iso27001" || standardParts[i] === "iso 27001") {
                            standardParts[i] = "iso";
                        }

                        if (standardParts[i] === normalizedCustomerPlan) {
                            standardMatches = true;
                            break;
                        }
                    }
                }

                let moduleIsActive = true; // Default to true if no data-module provided
                if (moduleAttr || standardAttr) {
                    const normalizedModuleAttr = (moduleAttr || standardAttr).toLowerCase();

                    // console.error('Testing module', normalizedModuleAttr, await isModuleEnabled(normalizedModuleAttr));
                    if (await isModuleEnabled(normalizedModuleAttr)) {
                        moduleIsActive = true;
                    } else {
                        moduleIsActive = false;
                    }
                }

                // Decide visibility:
                if (standardMatches || moduleIsActive) {
                    // Show the element if standard(customerPlan) matches OR module is active
                    element.style.display = '';
                } else {
                    // console.error('Hide', element)
                    // Otherwise, hide the element
                    element.style.display = 'none';
                    if (element.classList.contains('tab-pane')) {
                        element.classList.remove('show', 'active');
                    } else if (element.parentElement?.classList.contains('nav-item')) {
                        element.parentElement.remove();
                    } else {
                        // element.remove();
                    }
                }

                // Handle active state for tab-pane or button
                if (standardMatches && (!this.changedPage || !this.changedButton)) {
                    if (!this.changedPage && element.classList.contains('tab-pane')) {
                        element.classList.add('show', 'active');
                        this.changedPage = true;
                    }
                    if (!this.changedButton) {
                        const button = element.querySelector<HTMLButtonElement>('button');
                        if (button) {
                            button.classList.add('active');
                            this.changedButton = true;
                        }
                    }
                }
            }
        }
    }
    /**
     * Filters elements based on MSP (Managed Service Provider) status. Elements are hidden or shown accordingly.
     * @param isMsp - A boolean indicating whether MSP is enabled.
     */
    public filterByMsp(isMsp: boolean): void {
        this.resetFlags();

        const elements = this.pageView.querySelectorAll<HTMLElement>('[data-msp]');
        elements.forEach((element) => {
            const attrValue = element.getAttribute('data-msp');
            const attrIsTrue = attrValue === 'true';

            if (attrIsTrue && isMsp !== true) {
                // Hide or remove the element
                element.style.display = 'none';
                if (element.classList.contains('tab-pane')) {
                    element.classList.remove('show', 'active');
                } else {
                    element.remove();
                }
            } else if (attrIsTrue && isMsp === true) {
                if (!this.changedPage || !this.changedButton) {
                    if (!this.changedPage && element.classList.contains('tab-pane')) {
                        element.classList.add('show', 'active');
                        this.changedPage = true;
                    } else if (!this.changedButton) {
                        const button = element.querySelector<HTMLButtonElement>('button');
                        if (button) {
                            button.classList.add('active');
                            this.changedButton = true;
                        }
                    }
                }
            }
        });
    }

    /**
     * Replaces placeholders within the text content of the page view with specified replacement text.
     * @param placeholder - The placeholder text to search for (e.g., "{{placeholder}}").
     * @param replacementText - The text to replace the placeholder with.
     * @param content - The content to search within. If not provided, the entire page view is searched.
     */
    public replacePlaceholder(placeholder: string, replacementText: string, content: string | null = null): string | void {
        const placeholderRegex = new RegExp(`{{\\s*${this.escapeRegExp(placeholder)}\\s*}}`, 'gi');

        if (content !== null) {
            // Replace patterns in the provided content string
            let modifiedContent: string = content.replace(placeholderRegex, replacementText);
            return modifiedContent;
        } else {
            const walker = document.createTreeWalker(
                this.pageView,
                NodeFilter.SHOW_TEXT,
                null
            );

            let node: Text | null;
            while (((node as any) = walker.nextNode()) !== null) {
                if (placeholderRegex.test(node.nodeValue || '')) {
                    node.nodeValue = node.nodeValue?.replace(placeholderRegex, replacementText);
                }
            }
        }
    }

    /**
     * Replaces text within the page view based on a provided regular expression pattern.
     * This method allows for advanced text manipulations using RegExp patterns and replacement strings or functions.
     * @param pattern - The RegExp pattern to search for.
     * @param replacement - The replacement string.
     */
    public replaceWithRegex(pattern: RegExp, replacement: string, content: string | null): void;

    /**
     * Replaces text within the page view based on a provided regular expression pattern.
     * This method allows for advanced text manipulations using RegExp patterns and replacement strings or functions.
     * @param pattern - The RegExp pattern to search for.
     * @param replacement - The replacement function.
     * @param content - The content to search within. If not provided, the entire page view is searched.
     */
    public replaceWithRegex(pattern: RegExp, replacement: (substring: string, ...args: any[]) => string, content: string | null): void;

    /**
     * Implementation of the overloaded `replaceWithRegex` method.
     * @param pattern - The RegExp pattern to search for.
     * @param replacement - The replacement string or function.
     * @param content - The content to search within. If not provided, the entire page view is searched.
     */
    public replaceWithRegex(
        pattern: RegExp,
        replacement: string | ((substring: string, ...args: any[]) => string),
        content: string | null = null
    ): void | string {
        // Ensure the pattern has the global flag to replace all occurrences
        const flags = pattern.flags.includes('g') ? pattern.flags : pattern.flags + 'g';
        const globalPattern = new RegExp(pattern.source, flags);

        if (content !== null) {
            // Replace patterns in the provided content string
            let modifiedContent: string;
            if (typeof replacement === 'string') {
                modifiedContent = content.replace(globalPattern, replacement);
            } else {
                modifiedContent = content.replace(globalPattern, replacement);
            }
            return modifiedContent;
        } else {
            const walker = document.createTreeWalker(
                this.pageView,
                NodeFilter.SHOW_TEXT,
                null
            );

            let node: Text | null;
            while (((node as any) = walker.nextNode()) !== null) {
                if (globalPattern.test(node.nodeValue || '')) {
                    // Type guard to handle string or function replacement
                    if (typeof replacement === 'string') {
                        node.nodeValue = node.nodeValue.replace(globalPattern, replacement);
                    } else {
                        node.nodeValue = node.nodeValue.replace(globalPattern, replacement);
                    }
                }
            }
        }
    }


    /**
     * Converts specific clause texts within square brackets to styled badges.
     * Handles multiple clauses separated by semicolons.
     * For example:
     * [Clause 4.1: Understanding the organization and its context; Clause 4.2: Understanding the needs and expectations of interested parties]
     * becomes two separate badge elements.
     */
    public convertClausesToBadge(): void {
        // Updated regular expression to match [Clause X.Y.Z.A.B.C: Description; Clause X.Y.Z.A.B.C: Description; ...]
        const clauseRegex = /\[Clause\s+\d+(?:\.\d+)+:\s+[^\]]+(?:;\s*Clause\s+\d+(?:\.\d+)+:\s+[^\]]+)*\]/g;

        const walker = document.createTreeWalker(
            this.pageView,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode: (node) => {
                    if (clauseRegex.test(node.nodeValue || '')) {
                        return NodeFilter.FILTER_ACCEPT;
                    }
                    return NodeFilter.FILTER_SKIP;
                }
            }
        );

        const nodesToProcess: Text[] = [];

        // Collect all matching text nodes
        let currentNode: Text | null;
        while ((currentNode = walker.nextNode() as Text | null) !== null) {
            nodesToProcess.push(currentNode);
        }

        nodesToProcess.forEach((textNode) => {
            const parent = textNode.parentElement;
            if (!parent) return;

            const text = textNode.nodeValue || '';
            const fragments: (Text | HTMLElement)[] = [];
            let lastIndex = 0;

            // Iterate over all matches in the text node
            let match: RegExpExecArray | null;
            while ((match = clauseRegex.exec(text)) !== null) {
                const matchStart = match.index;
                const matchEnd = clauseRegex.lastIndex;

                // Text before the match
                if (matchStart > lastIndex) {
                    const beforeText = text.substring(lastIndex, matchStart);
                    fragments.push(document.createTextNode(beforeText));
                }

                // Extract the entire clause block without brackets
                const clauseBlock = match[0].substring(1, match[0].length - 1);

                // Split individual clauses by semicolon
                const individualClauses = clauseBlock.split(/\s*;\s*Clause\s+/);

                individualClauses.forEach((clause, index) => {
                    // For the first clause, 'Clause ' might still be prefixed
                    let clauseText = clause;

                    // Add 'Clause ' prefix for the first clause, unless it already has one (e.g., '4.1')
                    // This is to ensure consistent styling with other clause numbers in the text node.
                    if (!clauseText.startsWith('Clause ')) {
                        clauseText = 'Clause ' + clauseText;
                    }

                    // get clause id by extracting the (X.Y.Z.A.B.C) part
                    const clauseID = clauseText.match(/Clause\s+(\d+(?:\.\d+)+)\b/)?.[1];

                    // Ensure the clause id is valid
                    if (!clauseID) {
                        console.error(`Invalid clause ID: ${clauseText}`);
                        return;
                    }

                    // Create badge element
                    const badge = document.createElement('span');
                    badge.textContent = clauseText.trim();
                    badge.dataset.clauseId = clauseID;
                    badge.style.opacity = '0';
                    badge.style.display = 'none';
                    badge.classList.add('badge', 'clause'); // Ensure you have CSS for the 'badge' class)

                    fragments.push(badge);

                    // Optionally, add a space or separator between badges
                    fragments.push(document.createTextNode(' '));
                });

                lastIndex = matchEnd;
            }

            // Text after the last match
            if (lastIndex < text.length) {
                const afterText = text.substring(lastIndex);
                fragments.push(document.createTextNode(afterText));
            }

            // Remove the trailing space added after the last badge
            if (fragments.length > 0 && fragments[fragments.length - 1] instanceof Text) {
                const lastFragment = fragments[fragments.length - 1] as Text;
                if (lastFragment.nodeValue === ' ') {
                    fragments.pop();
                }
            }

            // Replace the original text node with the new fragments
            const fragment = document.createDocumentFragment();
            fragments.forEach(fragmentPart => fragment.appendChild(fragmentPart));
            parent.replaceChild(fragment, textNode);
        });
    }

    /**
     * Escapes special characters in a string to be used within a regular expression.
     * @param text - The string to escape.
     * @returns The escaped string.
     */
    private escapeRegExp(text: string): string {
        return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escapes special regex characters
    }
}